import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QUESTIONS } from '../../constants/questions';
import ProgressBar from './ProgressBar';
import OptionCard from './OptionCard';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../shared/SafeIcon';

export default function QuestionView({ onComplete }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [inputValue, setInputValue] = useState('');

  const currentQuestion = QUESTIONS[currentIndex];

  // Sync scroll to top on question change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentIndex]);

  // Handle manual "Next" or "Continue"
  const handleNext = (overrideValue = null) => {
    const finalAnswers = { ...answers };
    
    // Determine the value to save for this step
    if (overrideValue !== null) {
      finalAnswers[currentQuestion.id] = overrideValue;
    } else if (currentQuestion.type === 'text') {
      finalAnswers[currentQuestion.id] = inputValue;
    }
    // Multiple choice is already handled in state by handleOptionSelect

    if (currentIndex < QUESTIONS.length - 1) {
      setAnswers(finalAnswers);
      setInputValue(''); // Clear text input for next potential text question
      setCurrentIndex(prev => prev + 1);
    } else {
      // LAST QUESTION REACHED
      onComplete(finalAnswers);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      // Pre-fill previous answer if it was text
      const prevQuestion = QUESTIONS[currentIndex - 1];
      if (prevQuestion.type === 'text') {
        setInputValue(answers[prevQuestion.id] || '');
      }
    }
  };

  const handleOptionSelect = (option) => {
    if (currentQuestion.type === 'single') {
      // For single choice, we save and advance immediately
      handleNext(option);
    } else if (currentQuestion.type === 'multiple') {
      const currentSelections = answers[currentQuestion.id] || [];
      let newSelections;
      if (currentSelections.includes(option)) {
        newSelections = currentSelections.filter(item => item !== option);
      } else {
        newSelections = [...currentSelections, option];
      }
      setAnswers(prev => ({ ...prev, [currentQuestion.id]: newSelections }));
    }
  };

  const isNextDisabled = () => {
    if (currentQuestion.type === 'text') return !inputValue.trim();
    if (currentQuestion.type === 'single') return !answers[currentQuestion.id];
    if (currentQuestion.type === 'multiple') return !answers[currentQuestion.id] || answers[currentQuestion.id].length === 0;
    return false;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 bg-white">
      
      {/* Brand Header */}
      <div className="absolute top-6 left-6 sm:top-8 sm:left-8">
        <div className="flex items-center gap-2 text-[#7C3AED] font-bold text-2xl">
          <SafeIcon icon={FiIcons.FiHeart} className="text-3xl" />
          <span className="tracking-tight">Memovo</span>
        </div>
      </div>

      <div className="w-full max-w-3xl mb-8">
        <ProgressBar current={currentIndex} total={QUESTIONS.length} />
      </div>

      <div className="w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 p-8 sm:p-12 min-h-[520px] flex flex-col relative overflow-hidden">
        
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="flex-grow flex flex-col"
          >
            <h2 className="text-3xl sm:text-4xl font-[900] text-[#4C1D95] mb-3 leading-tight">
              {currentQuestion.title}
            </h2>
            {currentQuestion.subtitle && (
              <p className="text-slate-500 mb-10 text-lg leading-relaxed font-medium">
                {currentQuestion.subtitle}
              </p>
            )}

            <div className="mt-2 flex-grow">
              {currentQuestion.type === 'text' && (
                <div className="relative">
                  <input
                    type="text"
                    autoFocus
                    className="input-field"
                    placeholder={currentQuestion.placeholder}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !isNextDisabled() && handleNext()}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">
                    <SafeIcon icon={FiIcons.FiEdit3} />
                  </div>
                </div>
              )}

              {(currentQuestion.type === 'single' || currentQuestion.type === 'multiple') && (
                <div className="grid grid-cols-1 gap-4">
                  {currentQuestion.options.map((option) => {
                    const isSelected = currentQuestion.type === 'single' 
                      ? answers[currentQuestion.id] === option
                      : (answers[currentQuestion.id] || []).includes(option);

                    return (
                      <OptionCard
                        key={option}
                        label={option}
                        type={currentQuestion.type}
                        selected={isSelected}
                        onClick={() => handleOptionSelect(option)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 pt-8 border-t border-slate-50 flex items-center justify-between">
          <button
            onClick={handleBack}
            className={`flex items-center gap-2 font-bold text-slate-400 hover:text-slate-900 transition-colors ${currentIndex === 0 ? 'invisible' : ''}`}
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} />
            Back
          </button>
          
          {/* Continue button only shows for text or multiple choice. Single choice auto-advances. */}
          {(currentQuestion.type !== 'single') && (
            <button
              onClick={() => handleNext()}
              disabled={isNextDisabled()}
              className="btn-primary"
            >
              {currentIndex === QUESTIONS.length - 1 ? 'Check Funding' : 'Continue'}
              <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}